import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { SUPPORTED_LANGS, DEFAULT_LANG, LANG_TO_ISO, isSupportedLang } from '../i18n/lang.config';

/**
 * SeoService
 * -----------
 * Centraliza el SEO técnico de la SPA:
 *  - <title> y <meta name="description"> por ruta (traducibles vía ngx-translate)
 *  - <link rel="canonical"> incluyendo el segmento de idioma (/de/products)
 *  - <link rel="alternate" hreflang="…"> para cada idioma + x-default
 *  - etiquetas Open Graph (og:title, og:description, og:url)
 *  - atributo <html lang> sincronizado con el idioma activo
 *
 * El path de la URL usa los códigos internos de la app (/po/, /ch/…),
 * mientras que el atributo hreflang usa el código ISO correcto (pl, cs…).
 */
@Injectable({ providedIn: 'root' })
export class SeoService {

  private readonly baseUrl = 'https://eft-systems.de';

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private title: Title,
    private meta: Meta,
    private translate: TranslateService
  ) {}

  /**
   * Actualiza title + description + canonical + hreflang + OG para la ruta actual.
   * @param titleKey       clave de traducción (o texto) para el <title>
   * @param descriptionKey clave de traducción (o texto) para la description
   * @param fullPath       ruta actual incluyendo idioma (ej: '/de/products')
   */
  updateForRoute(titleKey: string, descriptionKey: string, fullPath: string): void {
    const cleanPath = (fullPath || '/').split('?')[0];
    const canonical = this.baseUrl + cleanPath;
    const pathWithoutLang = this.stripLang(cleanPath);

    this.translate.get([titleKey, descriptionKey]).subscribe(t => {
      const pageTitle = t[titleKey] || titleKey;
      const pageDesc = t[descriptionKey] || descriptionKey;

      this.title.setTitle(pageTitle);
      this.meta.updateTag({ name: 'description', content: pageDesc });

      this.meta.updateTag({ property: 'og:title', content: pageTitle });
      this.meta.updateTag({ property: 'og:description', content: pageDesc });
      this.meta.updateTag({ property: 'og:url', content: canonical });
    });

    this.setCanonical(canonical);
    this.setHreflang(pathWithoutLang);
  }

  /** Inserta/actualiza <link rel="canonical">. */
  setCanonical(url: string): void {
    let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /**
   * Emite <link rel="alternate" hreflang="…"> para todos los idiomas y x-default.
   * @param pathWithoutLang ruta sin el segmento de idioma (ej: '/products' o '/')
   */
  setHreflang(pathWithoutLang: string): void {
    // Limpia los alternantes previos
    const old = this.doc.querySelectorAll('link[rel="alternate"][hreflang]');
    old.forEach(n => n.parentNode && n.parentNode.removeChild(n));

    const suffix = pathWithoutLang === '/' ? '' : pathWithoutLang;

    const add = (hreflang: string, langSegment: string) => {
      const link = this.doc.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', `${this.baseUrl}/${langSegment}${suffix}`);
      this.doc.head.appendChild(link);
    };

    SUPPORTED_LANGS.forEach(code => add(LANG_TO_ISO[code] || code, code));
    add('x-default', DEFAULT_LANG);
  }

  /** Sincroniza <html lang> y og:locale con el idioma activo de la app. */
  setHtmlLang(appLangCode: string): void {
    const iso = LANG_TO_ISO[appLangCode] || DEFAULT_LANG;
    this.doc.documentElement.setAttribute('lang', iso);
    this.meta.updateTag({ property: 'og:locale', content: iso });
  }

  /** Elimina el primer segmento si es un idioma soportado: '/de/products' -> '/products'. */
  private stripLang(path: string): string {
    const parts = path.split('/').filter(p => p.length);
    if (parts.length && isSupportedLang(parts[0])) {
      parts.shift();
    }
    return '/' + parts.join('/');
  }
}
